// @ts-nocheck

import { chain } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { CloseHighligths } from '../../assets/svg';
import { Target } from '../../client';
import useRouter from '../../hooks/useRouter';
import { HighlightVideoPlayer } from '../../modules/immersive/components/video-player';
import theme from '../../styled-components/dark';
import ScrollView from '../ScrollView';

const Container = styled.div`
  background: ${theme.HighlightsContainer.background};
  display: flex;
  padding: 0.3em 10px 0;
  overflow: hidden;
  z-index: 12;
  position: ${(props) => (props.isSticky ? 'fixed' : 'relative')};

  ${({ $isSticky }) =>
    !Target.__MOBILE__ && !$isSticky
      ? css`
          margin: 0 10px;
          border-radius: 10px 10px 0 0;
        `
      : undefined}

  ${({ $isSticky }) =>
    !Target.__MOBILE__ && $isSticky
      ? css`
          width: 500px;
          z-index: 12;
          margin: 0;
          border-radius: 0;
        `
      : undefined}

  &::before {
    background: ${theme.HighlightsContainer.background};
    bottom: 0;
    content: '';
    left: -10px;
    position: absolute;
    top: 0;
    width: 20px;
    z-index: 1;
  }
`;

const ContainerPlayer = styled.div`
  ${({ $isSticky }) =>
    !Target.__MOBILE__ && !$isSticky
      ? css`
          margin: 0 10px;
        `
      : undefined}
`;

const InnerWrap = styled.div`
  display: inline-block;
  height: 100%;
  overflow: hidden;
  position: relative;
`;

const List = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  position: relative;
  transform-style: preserve-3d;
  white-space: nowrap;
  width: 100%;
`;

const StyledButton = styled.div`
  -webkit-tap-highlight-color: transparent;
  background: ${theme.HighlightFilters.primaryBackground};
  border-radius: 150em;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  margin-right: 0.6em;
  padding: 0.35em 0.8em;
  user-select: none;

  ${({ isSelected }) =>
    isSelected
      ? css`
          color: ${theme.HighlightFilters.selectedText};
        `
      : css`
          color: ${theme.HighlightFilters.primaryText};
        `}
`;

const ContainerCategory = styled.div`
  align-items: center;
  background: ${theme.HighlightsContainer.background};
  color: ${theme.HighlightsContainer.color};
  display: flex;
  flex: 0 0 auto;
  font-size: 0.95rem;
  margin-right: 0.6em;
  padding-right: 0.8em;
  padding-bottom: 0.3em;
  position: relative;
  z-index: 1;

  &::after {
    background: ${theme.HighlightFilters.separator};
    content: '';
    height: 14px;
    position: absolute;
    right: 0;
    width: 1px;
  }
`;

const CloseButton = styled.div`
  background: ${theme.HighlightFilters.closeSvgColor};
  border-radius: 50%;
  cursor: pointer;
  height: 30px;
  margin-right: 0.8em;
  text-align: center;
  width: 30px;
`;

const StyledCloseIcon = styled(CloseHighligths)`
  height: 10px;
  margin-top: 10px;
  width: 10px;
`;

type MatchToolbarHighlightsProps = {
  hasHighlights: boolean;
  highlights: Array<{ stream: string; eventType: object }>;
  highlightId: string;
  jwt?: {
    token: string;
  };
};

const MatchToolbarHighlights = ({
  highlights,
  jwt,
  setContentSize,
  isSticky,
  offsetTop,
  placeholderRef,
}: MatchToolbarHighlightsProps): Node => {
  const containerRef = useRef();
  const containerCategoryRef = useRef();
  const [highlightCategorySelected, setHighlightCategorySelected] = useState(null);
  const [highlightSelected, setHighlightSelected] = useState(null);
  const [calculWidth, setCalculWidth] = useState();
  const [streamId, setStreamId] = useState(null);
  const [timestamp, setTimestamp] = useState(new Date());
  const { history, location } = useRouter();

  const searchParams = new URLSearchParams(location.search);
  const highlightId = searchParams.get('highlightId');

  const measuredContentSize = (node) => {
    if (setContentSize) {
      setContentSize(node?.getBoundingClientRect()?.height);
    }
  };

  // Group highlight with the same event type
  const sortedHighlights = chain(highlights)
    .groupBy((h) => h.eventType.type)
    .map((value, key) => ({ label: value[0].eventType.name, type: key, events: value }))
    .value();

  useEffect(() => {
    const containerInfo = containerRef.current.getBoundingClientRect();

    if (highlightCategorySelected && containerRef.current) {
      const containerCategoryInfo = containerCategoryRef.current.getBoundingClientRect();
      return setCalculWidth(containerInfo.width - containerCategoryInfo.width - 20);
    }

    return setCalculWidth(containerInfo.width - 20);
  }, [highlightCategorySelected, containerCategoryRef, containerRef]);

  // Open most recent top Prio highlight by default
  useEffect(() => {
    if (!highlightSelected && sortedHighlights.length > 0) {
      const firstTopHighlight = sortedHighlights[0]?.events[sortedHighlights[0]?.events.length - 1];

      if (!firstTopHighlight) {
        return;
      }

      setHighlightCategorySelected(firstTopHighlight.eventType.type);
      setHighlightSelected(firstTopHighlight.stream);
    }
  }, [highlightSelected, sortedHighlights]);

  useEffect(() => {
    setStreamId(highlightSelected);
  }, [highlightSelected]);

  useEffect(() => {
    if (highlightId) {
      const highlight = highlights.find((h) => h.stream === highlightId);

      if (!highlight) {
        return;
      }

      setHighlightCategorySelected(highlight.eventType.type);
      setHighlightSelected(highlight.stream);

      // We delete the highlightId query params from the URL after the first load
      // to avoid reloading this highlight whe the user click on the Highlight toolbar icon
      const queryParams = new URLSearchParams(location.search);
      queryParams.delete('highlightId');
      history.replace({
        search: queryParams.toString(),
      });
    }
  }, [highlightId, highlights, history, location]);

  const onCategoryClick = (category) => {
    setHighlightCategorySelected(category);
  };

  const onHighlightClick = (highlight) => {
    setTimestamp(new Date());
    setHighlightSelected(highlight);
  };

  const onClose = () => {
    onCategoryClick(null);
  };

  const renderEvents = useCallback(() => {
    const events = sortedHighlights.find((h) => h.type === highlightCategorySelected).events;

    return events.reverse().map((event, key) => {
      return (
        <StyledButton
          key={event.stream}
          onClick={() => onHighlightClick(event.stream)}
          isSelected={highlightSelected === event.stream}
        >
          {event.playerName ? `${event.playerName} - ` : null}
          {event.minute ? `${event.minute}'` : `#${events.length - key}`}
        </StyledButton>
      );
    });
  }, [sortedHighlights, highlightCategorySelected, highlightSelected]);

  return (
    <div>
      <Container ref={containerRef} $isSticky={isSticky} offsetTop={offsetTop} placeholderRef={placeholderRef}>
        {highlightCategorySelected ? (
          <ContainerCategory ref={containerCategoryRef}>
            <CloseButton onClick={onClose}>
              <StyledCloseIcon />
            </CloseButton>
            <div>{sortedHighlights.find((h) => h.type === highlightCategorySelected).label}</div>
          </ContainerCategory>
        ) : null}

        <ScrollView vertical={false} style={{ width: `${calculWidth}px` }}>
          <InnerWrap>
            <List>
              {highlightCategorySelected
                ? renderEvents()
                : sortedHighlights.map((highlightCategory) => (
                    <StyledButton key={highlightCategory.type} onClick={() => onCategoryClick(highlightCategory.type)}>
                      {highlightCategory.label}
                    </StyledButton>
                  ))}
            </List>
          </InnerWrap>
        </ScrollView>
      </Container>
      <ContainerPlayer $isSticky={isSticky} ref={measuredContentSize}>
        {highlightSelected || streamId ? (
          <HighlightVideoPlayer key={timestamp} jwt={jwt} streamData={streamId} />
        ) : null}
      </ContainerPlayer>
    </div>
  );
};

export default MatchToolbarHighlights;
